import { Injectable } from '@angular/core';
import { PurchasedLicensesInfo } from '../data/purchased-licenses-info';

export interface FilteringOptions
{
    asOfDate: Date;
    ignoreLicenseDates: boolean;
    gracePeriodDays?: number;
    excludeGracePeriod?: boolean;
    selectedPurchaseType?: string;
    selectedPurchaseLevel?: string;
    sortField?: string;
    sortAscending?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LicenseFilteringService
{

    constructor() { }

    /**
     * Filters and sorts licenses based on the provided options
     * @param allLicenses - The complete list of licenses to filter
     * @param options - Filtering and sorting options
     * @returns Filtered and sorted licenses
     */
    filterAndSortLicenses(allLicenses: PurchasedLicensesInfo[], options: FilteringOptions): PurchasedLicensesInfo[]
    {
        if (!allLicenses || allLicenses.length === 0)
        {
            console.log('No licenses to filter and sort');
            return [];
        }
        else
        {
            console.log('starting filtering with ', allLicenses.length, ' licenses');
        }

        let filteredLicenses: PurchasedLicensesInfo[];

        if (options.ignoreLicenseDates)
        {
            // If ignoring license dates, show all licenses
            filteredLicenses = [...allLicenses];
        }
        else
        {
            // Apply date-based filtering (ignoring renewals for now)
            const asOfDateValue = options.asOfDate;

            // Calculate grace period for all licenses based on the current asOfDate
            this.calculateGracePeriodForAllLicenses(allLicenses, asOfDateValue, options.gracePeriodDays || 60);

            filteredLicenses = allLicenses.filter(license =>
                this.isDateWiseActive(license, asOfDateValue)
            );

            console.log('filteredLicenses count after date filtering:', filteredLicenses.length);
        }

        // Apply renewal filtering (remove licenses renewed by other active licenses)
        if (!options.ignoreLicenseDates)
        {
            filteredLicenses = filteredLicenses.filter(license =>
                !this.hasBeenRenewedByActiveLicense(license, filteredLicenses)
            );

            console.log('filteredLicenses count after renewal filtering:', filteredLicenses.length);
        }

        // Apply UI filters
        filteredLicenses = this.applyUIFilters(filteredLicenses, options);

        console.log('filteredLicenses count after applying UI filters:', filteredLicenses.length);

        // Apply sorting if specified
        if (options.sortField)
        {
            filteredLicenses = this.sortLicenses(filteredLicenses, options.sortField, options.sortAscending || true);
        }

        return filteredLicenses;
    }


    /**
     * Checks if a license is active based on date criteria
     */
     private isDateWiseActive(license: PurchasedLicensesInfo, asOfDate: Date): boolean
     {
         // Ensure asOfDate is a Date object
         const normalizedAsOfDate = asOfDate instanceof Date ? asOfDate : new Date(asOfDate);
         
         // Check if license has quantity
         if (!license.Quantity)
         {
             return false;
         }

         // Check if purchase date is before or on asOfDate
         const purchaseDate = this.parseDateAsLocal(license.PurchaseDate);
         if (purchaseDate > normalizedAsOfDate)
         {
             return false;
         }

         // Check if license is active (not expired or in grace period)
         const licenseEndDate = this.parseDateAsLocal(license.LicenseEndDate);

         const isActive = licenseEndDate >= normalizedAsOfDate || (license.IsInGracePeriod && !license.IsGracePeriodDisabled);

        return isActive;
    }


    /**
     * Checks if a license has been renewed by another active license
     */
    private hasBeenRenewedByActiveLicense(license: PurchasedLicensesInfo, activeLicenses: PurchasedLicensesInfo[]): boolean
    {
        // Check if any of the renewals are in the active licenses list
        return activeLicenses.some(al =>
            al.PurchasesRenewedByThisPurchase.some(r => r.PurchasedLicensesID === license.PurchasedLicensesID)
        );
    }

    /**
     * Calculates grace period status for all licenses
     */
     private calculateGracePeriodForAllLicenses(licenses: PurchasedLicensesInfo[], asOfDate: Date, gracePeriodDays: number): void
     {
         // Ensure asOfDate is a Date object
         const normalizedAsOfDate = asOfDate instanceof Date ? asOfDate : new Date(asOfDate);
         
         licenses.forEach(license =>
         {
             if (!license.LicenseEndDate)
             {
                 license.IsInGracePeriod = false;
                 return;
             }

             const licenseEndDate = this.parseDateAsLocal(license.LicenseEndDate);
             const gracePeriodEndDate = new Date(licenseEndDate);
             gracePeriodEndDate.setDate(gracePeriodEndDate.getDate() + gracePeriodDays);

             // A license is in grace period if:
             // 1. License has expired (end date < asOfDate)
             // 2. We're still within 60 days of the license expiration
             // Note: We assume grace period is always enabled unless there's a specific business rule
             license.IsInGracePeriod = licenseEndDate < normalizedAsOfDate && normalizedAsOfDate <= gracePeriodEndDate;
         });
     }

    /**
     * Applies UI-specific filters (grace period, purchase type, purchase level)
     */
    private applyUIFilters(licenses: PurchasedLicensesInfo[], options: FilteringOptions): PurchasedLicensesInfo[]
    {
        return licenses.filter(license =>
        {
            // Filter by grace period
            if (options.excludeGracePeriod && license.IsInGracePeriod)
            {
                return false;
            }

            // Filter by purchase type
            if (options.selectedPurchaseType && license.LicensePurchaseType !== options.selectedPurchaseType)
            {
                return false;
            }

            // Filter by purchase level
            if (options.selectedPurchaseLevel && license.PurchaseLevel !== options.selectedPurchaseLevel)
            {
                return false;
            }

            return true;
        });
    }

    /**
     * Sorts licenses by a specific field and direction
     */
    sortLicenses(licenses: PurchasedLicensesInfo[], sortField: string, ascending: boolean): PurchasedLicensesInfo[]
    {
        if (!licenses || licenses.length === 0)
        {
            return [];
        }

        return [...licenses].sort((a, b) =>
        {
            let aValue = this.getSortValue(a, sortField);
            let bValue = this.getSortValue(b, sortField);

            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            if (typeof aValue === 'string' && typeof bValue === 'string')
            {
                return ascending ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            if (aValue < bValue) return ascending ? -1 : 1;
            if (aValue > bValue) return ascending ? 1 : -1;
            return 0;
        });
    }

    /**
     * Gets the value to sort by for a specific field
     */
    private getSortValue(license: PurchasedLicensesInfo, sortField: string): any
    {
        switch (sortField)
        {
            case 'PurchasingOrganizationDescription':
                return license.PurchasingOrganizationDescription || '';
            case 'PurchaseDate':
                return license.PurchaseDate ? new Date(license.PurchaseDate) : null;
            case 'LicenseStartDate':
                return license.LicenseStartDate ? new Date(license.LicenseStartDate) : null;
            case 'LicenseEndDate':
                return license.LicenseEndDate ? new Date(license.LicenseEndDate) : null;
            case 'Quantity':
                return license.Quantity || 0;
            case 'PurchaseDollarAmount':
                return license.PurchaseDollarAmount || 0;
            case 'ARR':
                return license.ARR || 0;
            case 'LicenseLengthYears':
                return license.LicenseLengthYears || 0;
            case 'IsInGracePeriod':
                return license.IsInGracePeriod ? 1 : 0;
            default:
                return '';
        }
    }

    /**
     * Parses a date string as a local date to avoid timezone issues
     */
    private parseDateAsLocal(dateString: string | Date): Date
    {
        if (typeof dateString === 'string')
        {
            // Parse date string as local date (YYYY-MM-DD format)
            const [year, month, day] = dateString.split('-').map(Number);
            return new Date(year, month - 1, day); // month is 0-indexed
        }
        return new Date(dateString);
    }
}